import type { Column, ColumnGroup, TimeUnit } from './types'

const HOUR = 3600000

export function startOfUnit (date: Date, unit: TimeUnit): Date {
  const d = new Date(date.getTime())
  d.setMinutes(0, 0, 0)
  if (unit === 'day') {
    d.setHours(0)
  }
  return d
}

// Days are added with setDate so that local midnight survives DST changes
export function addUnits (date: Date, unit: TimeUnit, count: number): Date {
  if (unit === 'hour') {
    return new Date(date.getTime() + count * HOUR)
  }
  const d = new Date(date.getTime())
  d.setDate(d.getDate() + count)
  return d
}

export function isSameUnit (a: Date | null | undefined, b: Date | null | undefined, unit: TimeUnit): boolean {
  if (!a || !b) {
    return false
  }
  return startOfUnit(a, unit).getTime() === startOfUnit(b, unit).getTime()
}

export function buildColumns (unit: TimeUnit, windowStart: Date, count: number, now: Date = new Date()): Column[] {
  const columns: Column[] = []
  const todayStart = startOfUnit(now, unit).getTime()
  const todayEnd = addUnits(new Date(todayStart), unit, 1).getTime()
  let current = startOfUnit(windowStart, unit)
  for (let i = 0; i < count; i++) {
    const next = addUnits(current, unit, 1)
    const weekday = current.getDay()
    columns.push({
      start: current.getTime(),
      end: next.getTime(),
      date: current,
      weekend: unit === 'day' && (weekday === 0 || weekday === 6),
      today: current.getTime() >= todayStart && current.getTime() < todayEnd,
      past: next.getTime() <= todayStart
    })
    current = next
  }
  return columns
}

function findColumnIndex (columns: Column[], time: number): number {
  let lo = 0
  let hi = columns.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const col = columns[mid]
    if (time < col.start) {
      hi = mid - 1
    } else if (time >= col.end) {
      lo = mid + 1
    } else {
      return mid
    }
  }
  return -1
}

export function timeToX (time: number, columns: Column[], cellWidth: number): number {
  if (!columns.length) {
    return 0
  }
  const first = columns[0]
  const last = columns[columns.length - 1]
  if (time <= first.start) {
    return 0
  }
  if (time >= last.end) {
    return columns.length * cellWidth
  }
  const index = findColumnIndex(columns, time)
  const col = columns[index]
  return index * cellWidth + ((time - col.start) / (col.end - col.start)) * cellWidth
}

export function xToTime (x: number, columns: Column[], cellWidth: number): number {
  if (!columns.length) {
    return 0
  }
  const clamped = Math.max(0, Math.min(x, columns.length * cellWidth - 0.001))
  const index = Math.floor(clamped / cellWidth)
  const col = columns[index]
  const fraction = (clamped - index * cellWidth) / cellWidth
  return col.start + fraction * (col.end - col.start)
}

export function snapTime (time: number, unit: TimeUnit, snapMinutes: number, mode: 'round' | 'floor' | 'ceil' = 'round'): number {
  const date = new Date(time)
  if (unit === 'day') {
    const start = startOfUnit(date, 'day')
    if (mode === 'floor' || (mode === 'round' && date.getHours() < 12)) {
      return start.getTime()
    }
    if (mode === 'ceil' && start.getTime() === time) {
      return time
    }
    return addUnits(start, 'day', 1).getTime()
  }
  const step = snapMinutes * 60000
  const offset = date.getTimezoneOffset() * 60000
  const local = time - offset
  const fn = mode === 'floor' ? Math.floor : mode === 'ceil' ? Math.ceil : Math.round
  return fn(local / step) * step + offset
}

export function shiftByUnits (date: Date, unit: TimeUnit, units: number, snapMinutes: number): Date {
  if (unit === 'day') {
    return addUnits(date, 'day', Math.round(units))
  }
  const minutes = Math.round((units * 60) / snapMinutes) * snapMinutes
  return new Date(date.getTime() + minutes * 60000)
}

export function groupColumns (columns: Column[], unit: TimeUnit): ColumnGroup[] {
  const groups: ColumnGroup[] = []
  let current: ColumnGroup | null = null
  let currentKey: number | null = null
  for (let i = 0; i < columns.length; i++) {
    const d = columns[i].date
    const key = unit === 'day' ? d.getFullYear() * 12 + d.getMonth() : startOfUnit(d, 'day').getTime()
    if (!current || currentKey !== key) {
      current = { date: d, startIndex: i, count: 0 }
      currentKey = key
      groups.push(current)
    }
    current.count++
  }
  return groups
}
